import UserSideBar from "../../common/UserSidebar/UserSideBar"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { getPackages } from "../../redux/packagesSlice"
import { 
  getWallet, 
  addCredits, 
  selectWallet, 
  selectAddCreditsLoading, 
  selectAddCreditsError,
  clearAddCreditsError 
} from "../../redux/walletSlice"
import { useTranslation } from "react-i18next"

const MyWallet = () => {
  const dispatch = useDispatch();
  const [loadingPackageId, setLoadingPackageId] = useState(null);
  const { t } = useTranslation();

  const packagesState = useSelector((state) => state.packages);
  const wallet = useSelector(selectWallet);
  const addCreditsLoading = useSelector(selectAddCreditsLoading);
  const addCreditsError = useSelector(selectAddCreditsError);

  const { packages, loading: packagesLoading } = packagesState;


  useEffect(() => {
    dispatch(getPackages());
    dispatch(getWallet());
  }, [dispatch]);

  // Error temizleme
  useEffect(() => {
    if (addCreditsError) {
      const timer = setTimeout(() => {
        dispatch(clearAddCreditsError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [addCreditsError, dispatch]);

  const handleAddCredits = async (packageData) => {
    setLoadingPackageId(packageData._id);
    
    try {
      await dispatch(addCredits({
        packageId: packageData._id,
        type: packageData.appType, // video, voice, message
        amount: parseInt(packageData.quantity)
      })).unwrap();
      
      // Başarılı olursa wallet'ı yeniden yükle
      dispatch(getWallet());
    } catch (error) {
      console.error("Credit ekleme hatası:", error);
    } finally {
      setLoadingPackageId(null);
    }
  };

  const getPackageTypeLabel = (appType) => {
    switch (appType) {
      case 'video':
        return 'Video Meeting';
      case 'voice':
        return 'Audio Meeting';
      case 'message':
        return 'Texting';
      default:
        return appType;
    }
  };

  const getPackageName = (session) => {
    if (session.name !== 'string') return session.name;
    
    const quantity = parseInt(session.quantity) || 1;
    const type = session.appType || 'video';
    
    const key = quantity === 1 ? `single_${type}_session` : `multiple_${type}_sessions`;
    return t(key, { count: quantity });
  };

  const getDuration = (session) => {
    return parseInt(session.duration) || 45;
  };

  const formatCredits = (credits) => {
    if (!credits) return 'Loading...';
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-800">Video Credits</h4>
          <p className="text-2xl font-bold text-blue-600">{credits.video || 0}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="font-semibold text-green-800">Audio Credits</h4>
          <p className="text-2xl font-bold text-green-600">{credits.voice || 0}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h4 className="font-semibold text-purple-800">Message Credits</h4>
          <p className="text-2xl font-bold text-purple-600">{credits.message || 0}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-row gap-10 justify-center mt-[-50px]">
      <div>
        <UserSideBar />
      </div>

      <div className="max-w-[1200px] w-full">
        <div className="mt-[45px] space-y-8">
          
          {/* Wallet Bilgileri */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Wallet Information</h2>
            
            {/* Mevcut Krediler */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Current Credits</h3>
              {formatCredits(wallet?.credits)}
            </div>
          </div>

          {/* Error Message */}
          {addCreditsError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Credit Add Error
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {typeof addCreditsError === 'string' ? addCreditsError : addCreditsError.message || 'An error occurred'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Paketler */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Current Packages</h2>
            
            {packagesLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Packages are loading...</p>
              </div>
            ) : packages && packages.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packages.map((pkg) => (
                  <div key={pkg._id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="space-y-4">
                      {/* Package Header */}
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800">{getPackageName(pkg)}</h3>
                        <span className="inline-block px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full mt-2">
                          {getPackageTypeLabel(pkg.appType)}
                        </span>
                      </div>

                      {/* Package Details */}
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Credit Amount:</span>
                          <span className="font-semibold">{pkg.quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Duration:</span>
                          <span className="font-semibold">{getDuration(pkg)} {t("minuteAbbr")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Type:</span>
                          <span className="font-semibold capitalize">{pkg.appType}</span>
                        </div>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => handleAddCredits(pkg)}
                        disabled={loadingPackageId === pkg._id || addCreditsLoading}
                        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                          loadingPackageId === pkg._id || addCreditsLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {loadingPackageId === pkg._id ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Adding...
                          </div>
                        ) : (
                          'Add Credits'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No packages found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyWallet;
